import React, { useCallback, useMemo } from 'react';
import { Breadcrumb as AntBreadcrumb } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MenuItemType } from 'types';
import { buildBreadcrumbTrail } from 'utils/menuUtils';
import { ButtonCommon } from 'components/common';
import { ArrowLeft } from '@phosphor-icons/react';

interface BreadcrumbItem {
  title: React.ReactNode;
  path?: string;
  icon?: React.ReactNode;
  hasPage?: boolean;
}

interface BreadcrumbProps {
  items?: BreadcrumbItem[];
  menuItems?: MenuItemType[];
  className?: string;
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ items = [], menuItems = [], className = '' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathSnippets = location.pathname.split('/').filter((i) => i);
  let breadcrumbItems: BreadcrumbItem[] = [];

  if (menuItems.length > 0) {
    const breadcrumbTrail = buildBreadcrumbTrail(menuItems, location.pathname);

    breadcrumbItems = [
      ...breadcrumbTrail.map((item) => ({
        title: item.label,
        path: item.path,
        icon: item.icon,
        hasPage: item.hasPage,
      })),
    ];
  } else if (items.length > 0) {
    breadcrumbItems = items;
  } else {
    breadcrumbItems = [
      ...pathSnippets.map((snippet, index) => {
        const url = `/${pathSnippets.slice(0, index + 1).join('/')}`;
        return {
          title: snippet.charAt(0).toUpperCase() + snippet.slice(1).replace('-', ' '),
          path: url,
        };
      }),
    ];
  }

  // Helper function to find valid parent (hasPage !== false)
  const handleNavigateBack = useCallback(() => {
    // const currentSearchParams = window.location.search;
    if (breadcrumbItems.length > 1) {
      const previousPage = breadcrumbItems[breadcrumbItems.length - 2];
      if (previousPage.path) {
        const backPath = previousPage.path;
        // Nếu trùng path hiện tại, lùi thêm một cấp
        if (backPath === `/${pathSnippets?.join('/')}` || previousPage?.hasPage === false) {
          const thirdBack = breadcrumbItems[breadcrumbItems.length - 3];
          if (thirdBack?.path) {
            const path = thirdBack.path;
            navigate(path);
            return;
          }
        }
        navigate(backPath);
        return;
      }
    }
    navigate('/dashboard');
  }, [breadcrumbItems, pathSnippets, navigate]);
  // Memoize the breadcrumb items for AntBreadcrumb to prevent unnecessary re-renders
  const antBreadcrumbItems = useMemo(() => {
    return breadcrumbItems.map((item, index) => ({
      key: index.toString(),
      title:
        item.hasPage === false ? (
          <div
            className={`items-center gap-2 text-[14px] leading-[20px] !text-black ${
              index === breadcrumbItems.length - 1 ? 'font-bold' : ''
            }`}
            style={{ display: 'inline-flex' }}
          >
            <span>{item.title}</span>
          </div>
        ) : (
          <>
            {item.path && (
              <Link
                to={item.path}
                className={`items-center gap-2 text-[14px] leading-[20px] !text-black ${
                  index === breadcrumbItems.length - 1 ? 'font-bold' : ''
                }`}
                style={{ display: 'inline-flex' }}
              >
                {item.icon && breadcrumbItems.length === 1 && <span>{item.icon}</span>}
                <span>{item.title}</span>
              </Link>
            )}
          </>
        ),
    }));
  }, [breadcrumbItems]);
  return breadcrumbItems.length > 0 && breadcrumbItems[0].path !== '/' ? (
    <div
      className={`breadcrumb-container bg-white flex items-center border-b-[1.5px] border-[var(--textBlack8)]  py-2 px-6 ${className} h-[56px]`}
    >
      {breadcrumbItems.length > 1 && (
        <ButtonCommon
          icon={<ArrowLeft size={20} />}
          onClick={handleNavigateBack}
          color='soft'
          radius='rounded'
          className='mr-4'
        >
          Trở Về
        </ButtonCommon>
      )}
      <AntBreadcrumb separator='/' items={antBreadcrumbItems} />
    </div>
  ) : (
    <></>
  );
};

export default Breadcrumb;
